import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Plus, Trash2, Monitor, Smartphone, Code2, Copy, Check, Upload,
  AlertTriangle, XCircle, CheckCircle2, ArrowRight, ArrowLeft, Info,
  MessageSquarePlus, Send, X, Lock
} from "lucide-react";

/* Single source of truth. This is the same compiler the build scripts and the
   golden tests import — there is no second copy to drift. */
import {
  BRANDS, BLOCKS, GROUPS, STARTERS, B, compile, review, preflight,
  validate, repair,
} from "./compiler/index.js";
import { processImage, kb, IMAGE_BUDGET } from "./lib/image.js";
import { VERSION } from "./version.js";
import { report, LEVELS, EVENTS } from "./lib/report.js";

export default function EmailComposer() {
  const [step, setStep] = useState(0);
  const [role, setRole] = useState("requester");
  const [brand, setBrand] = useState("texans");
  const [tier, setTier] = useState("marketing");
  const [plan, setPlan] = useState({ goal: "", audience: "" });
  const [meta, setMeta] = useState({ name: "Untitled request", subject: "", preheader: "", emailGroupId: "", notes: "" });
  const [blocks, setBlocks] = useState([]);
  const [sel, setSel] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [showCode, setShowCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(null);
  const [imgErr, setImgErr] = useState(null);
  const [ask, setAsk] = useState(false);
  const [askText, setAskText] = useState("");
  const [asks, setAsks] = useState([]);
  const frame = useRef(null);
  const [previewDead, setPreviewDead] = useState(false);

  /* Versioned key: if the block library changes shape, old drafts are ignored
     rather than loaded into a layout that no longer matches. */
  const DRAFT_KEY = "texans-composer-draft-v1";
  const [foundDraft, setFoundDraft] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saveMode, setSaveMode] = useState("full");
  const [storageOff, setStorageOff] = useState(false);
  const [draftCorrupt, setDraftCorrupt] = useState(false);

  /* Look for a draft on mount but do not prefill. Silently restoring means you
     can't tell whether you're looking at your work or something stale. */
  useEffect(() => {
    let raw = null;
    try { raw = localStorage.getItem(DRAFT_KEY); }
    catch (e) { setStorageOff(true); report(LEVELS.warn, EVENTS.STORAGE_BLOCKED, { message: String(e) }); return; }   /* private browsing, or blocked */
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      if (!d?.blocks?.length) return;
      /* A draft can outlive the block library that produced it. Say so rather
         than opening it with sections silently missing. */
      const problems = validate(d).filter((x) => x.level !== "note");
      setFoundDraft({ ...d, problems });
    } catch (e) {
      setDraftCorrupt(true);
      report(LEVELS.silent, EVENTS.DRAFT_CORRUPT, { message: String(e) });
    }
  }, []);

  useEffect(() => {
    const l = document.createElement("link"); l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Barlow+Condensed:wght@600;700&family=IBM+Plex+Mono:wght@400&display=swap";
    document.head.appendChild(l);
    return () => l.parentNode && l.parentNode.removeChild(l);
  }, []);

  const B_ = BRANDS[brand];

  useEffect(() => {
    if (step === 0 || !blocks.length) return;
    const t = setTimeout(() => {
      const base = { savedAt: Date.now(), brand, tier, plan, meta, step };
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...base, blocks }));
        setSavedAt(Date.now()); setSaveMode("full");
      } catch (e) {
        /* Uploaded pictures are held as base64. Three images plus a card grid
           can push the draft past the browser's storage quota, and Safari is
           stricter than most. Rather than failing silently, save everything
           except the image data and say so. */
        try {
          const light = blocks.map((b) => String(b.slots?.src || "").startsWith("data:")
            ? { ...b, slots: { ...b.slots, src: "" }, imageBytes: 0, imageMeta: null }
            : b);
          localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...base, blocks: light, imagesDropped: true }));
          setSavedAt(Date.now()); setSaveMode("light");
          report(LEVELS.warn, EVENTS.DRAFT_SAVE_DEGRADED, { brand, context: `${blocks.length} blocks` });
        } catch (e2) {
          setSaveMode("failed");
          report(LEVELS.critical, EVENTS.DRAFT_SAVE_FAILED, { brand, context: `${blocks.length} blocks`, message: String(e2) });
        }
      }
    }, 600);
    return () => clearTimeout(t);
  }, [brand, tier, plan, meta, blocks, step]);

  const resumeDraft = () => {
    const { doc, removed } = repair(foundDraft);
    setBrand(doc.brand); setTier(doc.tier); setPlan(doc.plan); setMeta(doc.meta);
    setBlocks(doc.blocks); setStep(foundDraft.step || 1); setSel(null);
    if (removed.length) {
      report(LEVELS.silent, EVENTS.BLOCKS_DROPPED, { brand: doc.brand, context: removed.join(", ") });
      setMeta((mm) => ({
        ...mm,
        notes: `${mm.notes ? mm.notes + "\n\n" : ""}Restored from a draft. ${removed.length} block${removed.length === 1 ? "" : "s"} could not be restored because the library changed: ${removed.join(", ")}.`,
      }));
    }
    setFoundDraft(null);
  };
  const discardDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
      if (localStorage.getItem(DRAFT_KEY)) setStorageOff(true);   /* refused */
    } catch (e) { setStorageOff(true); }
    setFoundDraft(null);
  };
  const savedAgo = (ts) => {
    const m = Math.round((Date.now() - ts) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    return new Date(ts).toLocaleDateString();
  };
  const doc = useMemo(() => ({ brand, tier, plan, meta, blocks }), [brand, tier, plan, meta, blocks]);
  const clean = useMemo(() => compile(doc), [doc]);
  /* The preview is fed to an iframe via srcDoc, so any change reloads it. On a
     twenty-block email with pictures that payload is over a megabyte, and
     recompiling per keystroke meant a reload — and a flicker — on every
     character. Compiling is cheap (under a millisecond); reloading is not.
     Debounced so typing is smooth and the preview settles a beat later. */
  const livePreview = useMemo(() => compile(doc, { markers: true, footer: true }), [doc]);
  const [preview, setPreview] = useState(livePreview);
  useEffect(() => {
    const t = setTimeout(() => setPreview(livePreview), 220);
    return () => clearTimeout(t);
  }, [livePreview]);
  const { yours, team } = useMemo(() => review(doc), [doc]);
  const checks = useMemo(() => preflight(clean), [clean]);
  const stops = yours.filter((f) => f.level === "stop");
  const looks = yours.filter((f) => f.level === "check");
  const imgTotal = blocks.reduce((n, x) => n + (x.imageBytes || 0), 0);

  /* Inline controls live inside the preview, attached to the block they act on.
     A strip of names at the bottom of the screen made people guess which chip
     matched which part of the email. */
  const wire = () => {
    const f = frame.current;
    if (!f) return;
    try {
      const d = f.contentDocument;
      if (!d || !d.body) return;

      /* The preview is real email HTML with real hrefs. Without this, clicking
         a block also follows its link and takes the requester out of the app
         mid-build. Selection must never navigate. */
      d.querySelectorAll("a[href]").forEach((a) => {
        if (a.dataset.noNav) return;
        a.dataset.noNav = "1";
        a.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); }, true);
      });

      d.querySelectorAll("[data-blk]").forEach((el) => {
        const id = el.getAttribute("data-blk");
        const isSel = id === sel;
        el.style.position = "relative";
        el.style.cursor = "pointer";
        el.style.outlineOffset = "-2px";
        el.style.outline = isSel ? "2px solid #0080C6" : "";

        el.onclick = (e) => { e.stopPropagation(); setSel(id); };
        el.onmouseenter = () => {
          if (!isSel) el.style.outline = "2px solid rgba(0,128,198,.45)";
          if (el.offsetHeight < 48) el.style.background = "rgba(0,128,198,.10)";
          el.querySelector("[data-tools]")?.style.setProperty("display", "flex");
        };
        el.onmouseleave = () => {
          el.style.outline = isSel ? "2px solid #0080C6" : "";
          el.style.background = "";
          if (!isSel) el.querySelector("[data-tools]")?.style.setProperty("display", "none");
        };

        /* toolbar pinned to the block itself */
        let bar = el.querySelector("[data-tools]");
        if (!bar) {
          const host = el.querySelector("td") || el;
          host.style.position = "relative";
          bar = d.createElement("div");
          bar.setAttribute("data-tools", "1");
          /* A spacer is 32px tall and a divider thinner still. A 24px toolbar
             pinned to the top corner overflows or clips on those, so short
             blocks get a smaller bar centred vertically instead. */
          const thin = el.offsetHeight < 48;
          bar.style.cssText = [
            "position:absolute", "right:4px", "z-index:9",
            thin ? "top:50%" : "top:4px",
            thin ? "transform:translateY(-50%)" : "",
            "display:none", "gap:3px", "font-family:Barlow,system-ui,sans-serif",
          ].filter(Boolean).join(";");
          bar.dataset.thin = thin ? "1" : "0";

          const mk = (label, title, danger) => {
            const btn = d.createElement("button");
            btn.type = "button";
            btn.title = title;
            btn.textContent = label;
            const size = thin ? 18 : 24;
            btn.style.cssText = [
              "border:0", "border-radius:3px", "cursor:pointer", "padding:0",
              `width:${size}px`, `height:${size}px`, "line-height:1",
              `font-size:${thin ? 11 : 13}px`,
              `background:${danger ? "#ED0028" : "rgba(255,255,255,.92)"}`,
              `color:${danger ? "#fff" : "#021018"}`,
              "box-shadow:0 1px 3px rgba(0,0,0,.35)",
            ].join(";");
            return btn;
          };

          const up = mk("\u2191", "Move up");
          const down = mk("\u2193", "Move down");
          const del = mk("\u00d7", "Remove this block", true);
          up.onclick = (e) => { e.stopPropagation(); nudge(id, -1); };
          down.onclick = (e) => { e.stopPropagation(); nudge(id, 1); };
          del.onclick = (e) => { e.stopPropagation(); kill(id); };
          bar.append(up, down, del);
          host.appendChild(bar);
        }
        bar.style.display = isSel ? "flex" : "none";
      });

      f.style.height = d.body.scrollHeight + 20 + "px";
      setPreviewDead(false);
    } catch (e) {
      /* If wiring fails the preview looks fine and does nothing. Say so rather
         than leaving someone clicking a dead pane. */
      setPreviewDead(true);
      report(LEVELS.critical, EVENTS.PREVIEW_DEAD, { brand, message: String(e) });
    }
  };

  /* Was running on every render. The iframe reloads when the compiled preview
     changes and calls wire from onLoad; the only other trigger is selection. */
  useEffect(wire, [preview, sel]);

  /* Blocks are scoped by tier, not brand, so they survive a brand change. The
     email group does not — group 17 is a Texans group and means nothing to
     LSSE — so it is cleared rather than silently carried across. */
  const switchBrand = (next) => {
    if (next === brand) return;
    const groups = BRANDS[next].groups.map(([id]) => id);
    setBrand(next);
    setMeta((m) => ({ ...m, emailGroupId: groups.includes(m.emailGroupId) ? m.emailGroupId : "" }));
    setBlocks((p) => p.map((b) => ({ ...b, slots: { ...b.slots } })));
  };

  const start = (s) => { setTier(s.tier); setBlocks(s.make(brand)); setSel(null); setStep(1); setMeta((m) => ({ ...m, name: s.name })); };
  /* Insert after the selected block rather than at the end. Gutenberg's
     insert-before / insert-after gestures work this way and people expect it —
     appending to the bottom means a header lands under the footer. */
  const add = (t) => {
    const x = B(t, brand);
    setBlocks((p) => {
      const i = p.findIndex((y) => y.id === sel);
      if (i === -1) return [...p, x];
      return [...p.slice(0, i + 1), x, ...p.slice(i + 1)];
    });
    setSel(x.id);
  };
  const set = (id, k, v) => setBlocks((p) => p.map((x) => x.id === id ? { ...x, slots: { ...x.slots, [k]: v } } : x));
  const kill = (id) => { setBlocks((p) => p.filter((x) => x.id !== id)); setSel(null); };
  const nudge = (id, dir) => setBlocks((p) => {
    const i = p.findIndex((x) => x.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= p.length) return p;
    const n = [...p]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  /* Every finding knows what fixes it. Clicking one takes you there rather
     than leaving you to work out where "there" is. */
  const fieldRefs = useRef({});
  const jumpTo = (target) => {
    if (!target) return;
    if (target.block) setSel(target.block);
    else setSel(null);
    setTimeout(() => {
      const el = fieldRefs.current[target.field];
      if (el) { el.focus(); el.scrollIntoView({ block: "center", behavior: "smooth" }); }
    }, 60);
  };

  const onImage = async (id, file) => {
    const d = BLOCKS[blocks.find((x) => x.id === id).type];
    setImgErr(null); setBusy(id);
    try {
      const r = await processImage(file, d.image || "hero");
      setBlocks((p) => p.map((x) => x.id === id ? { ...x, slots: { ...x.slots, src: r.dataUrl }, imageBytes: r.bytes, imageMeta: r } : x));
    } catch (e) {
      setImgErr(e.message);
      report(LEVELS.warn, EVENTS.IMAGE_FAILED, { brand, context: d.label, message: e.message });
    } finally { setBusy(null); }
  };

  /* This one matters more than it looks. If the copy silently fails, the email
     team pastes whatever was on the clipboard before into Eloqua — potentially
     a different email entirely. Never report success we haven't confirmed. */
  const [copyFailed, setCopyFailed] = useState(false);
  const copyHtml = async () => {
    setCopyFailed(false);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clean);
        const back = await navigator.clipboard.readText?.().catch(() => null);
        if (back !== null && back !== clean) throw new Error("clipboard mismatch");
      } else {
        const ta = document.createElement("textarea");
        ta.value = clean; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("execCommand refused");
      }
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopyFailed(true);
      report(LEVELS.critical, EVENTS.COPY_FAILED, { brand, context: "email team copying HTML for Eloqua", message: String(e) });
    }
  };

  const cur = blocks.find((x) => x.id === sel);
  const palette = Object.entries(BLOCKS).filter(([, d]) => d.tiers.includes(tier));

  return (
    <div className="ec">
      <style>{`
        .ec{--ink:#021018;--red:#ED0028;--blue:#0080C6;--line:#E1E6E9;--line2:#EDF0F2;--sub:#5D6D77;--bg:#F7F8F9;
            font-family:'Barlow',system-ui,sans-serif;color:var(--ink);background:#fff;border:1px solid var(--line);
            border-radius:8px;height:900px;max-height:93vh;display:flex;flex-direction:column;overflow:hidden;position:relative;}
        .ec *{box-sizing:border-box;} .ec :focus-visible{outline:2px solid var(--blue);outline-offset:2px;}
        .ec button{font-family:inherit;}
        .lbl{font-family:'Barlow Condensed',sans-serif;text-transform:uppercase;letter-spacing:.12em;font-weight:600;font-size:11px;color:var(--sub);}
        .mono{font-family:'IBM Plex Mono',monospace;}
        .top{display:flex;align-items:center;gap:12px;padding:0 16px;height:56px;border-bottom:1px solid var(--line);flex:0 0 auto;}
        .brandpill{display:flex;align-items:center;gap:7px;border:1px solid var(--line);border-radius:20px;padding:4px 12px 4px 6px;font-size:13px;}
        .dot{width:12px;height:12px;border-radius:50%;}
        .steps{display:flex;gap:2px;}
        .stp{display:flex;align-items:center;gap:6px;border:0;background:none;cursor:pointer;padding:6px 11px;border-radius:4px;color:var(--sub);font-size:13px;}
        .stp:hover{background:var(--bg);} .stp[data-on="true"]{background:#E8F4FA;color:var(--blue);font-weight:600;}
        .stp b{display:grid;place-items:center;width:18px;height:18px;border-radius:50%;background:#E1E6E9;color:#fff;font-size:10px;font-weight:700;}
        .stp[data-on="true"] b{background:var(--blue);}
        .roles{display:flex;background:var(--bg);border-radius:4px;padding:2px;}
        .roles button{border:0;background:none;cursor:pointer;padding:5px 10px;border-radius:3px;font-size:12px;color:var(--sub);}
        .roles button[data-on="true"]{background:#fff;color:var(--ink);font-weight:600;box-shadow:0 1px 2px rgba(2,16,24,.12);}
        .body{flex:1 1 auto;min-height:0;display:flex;}
        .pane{flex:1;overflow-y:auto;padding:28px 20px 60px;background:var(--bg);}
        .inner{max-width:720px;margin:0 auto;}
        .card{background:#fff;border:1px solid var(--line);border-radius:6px;padding:22px;margin-bottom:14px;}
        .card h3{margin:0 0 4px;font-size:17px;font-weight:600;} .card p.sub{margin:0 0 16px;font-size:13.5px;line-height:1.55;color:var(--sub);}
        .tiles{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
        .tile{text-align:left;border:1px solid var(--line);background:#fff;border-radius:5px;padding:12px 14px;cursor:pointer;position:relative;}
        .tile:hover{border-color:#BBDDF0;} .tile[data-on="true"]{border-color:var(--blue);background:#F4FAFD;}
        .tile[data-off="true"]{opacity:.55;}
        .tile b{display:block;font-size:14px;font-weight:600;margin-bottom:2px;} .tile span{font-size:12.5px;line-height:1.5;color:var(--sub);}
        .tile em{display:block;font-style:normal;font-size:11px;color:var(--blue);margin-top:6px;}
        .tip{display:flex;gap:9px;background:#FFF6E8;border:1px solid #F2DFBE;border-radius:5px;padding:11px 13px;margin-top:14px;}
        .tip p{margin:0;font-size:12.5px;line-height:1.55;color:#7A5A20;}
        .rail{width:200px;flex:0 0 200px;border-right:1px solid var(--line);overflow-y:auto;padding:10px 0 24px;background:#FCFDFD;}
        .rg{padding:13px 14px 3px;}
        .pb{display:flex;align-items:center;gap:8px;width:calc(100% - 16px);margin:1px 8px;padding:7px 9px;background:none;border:0;border-radius:4px;cursor:pointer;font-size:13.5px;color:var(--ink);text-align:left;}
        .pb:hover{background:#EFF6FA;} .pb svg{color:var(--blue);flex:0 0 auto;}
        .askbtn{margin:14px 8px 0;padding:9px;border:1px dashed var(--line);border-radius:5px;background:none;cursor:pointer;font-size:12.5px;color:var(--sub);display:flex;align-items:center;gap:7px;justify-content:center;width:calc(100% - 16px);}
        .askbtn:hover{border-color:var(--blue);color:var(--blue);}
        .stage{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;background:var(--bg);}
        .sbar{display:flex;align-items:center;gap:7px;padding:0 14px;height:44px;border-bottom:1px solid var(--line);background:#fff;flex:0 0 auto;}
        .chip{display:flex;align-items:center;gap:5px;background:#fff;border:1px solid var(--line);color:var(--sub);border-radius:4px;padding:5px 10px;cursor:pointer;font-size:12.5px;}
        .chip:hover{border-color:#BBDDF0;color:var(--ink);} .chip[data-on="true"]{border-color:var(--blue);color:var(--blue);background:#F4FAFD;}
        .scroll{flex:1 1 auto;overflow-y:auto;padding:18px 16px 50px;}
        .paper{margin:0 auto;box-shadow:0 2px 14px rgba(2,16,24,.13);}
        .list{border-top:1px solid var(--line);background:#fff;flex:0 0 auto;padding:8px 14px;display:flex;gap:6px;overflow-x:auto;align-items:center;}
        .lc{display:flex;align-items:center;gap:6px;border:1px solid var(--line);border-radius:4px;padding:5px 9px;font-size:12px;background:#fff;cursor:grab;white-space:nowrap;color:var(--sub);}
        .lc[data-on="true"]{border-color:var(--blue);color:var(--blue);background:#F4FAFD;}
        .insp{width:342px;flex:0 0 342px;border-left:1px solid var(--line);display:flex;flex-direction:column;min-height:0;background:#fff;}
        .iscroll{flex:1 1 auto;overflow-y:auto;padding:18px;}
        .f{margin-bottom:15px;} .f label{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:5px;}
        .inp,.ta,.sel{width:100%;border:1px solid var(--line);border-radius:4px;padding:9px 11px;font-family:'Barlow',sans-serif;font-size:14px;color:var(--ink);background:#fff;}
        .ta{min-height:80px;resize:vertical;line-height:1.6;} .inp:focus,.ta:focus,.sel:focus{border-color:var(--blue);}
        .help{font-size:12px;line-height:1.5;color:var(--sub);margin:5px 0 0;}
        .cnt{font-size:11px;color:var(--sub);font-family:'IBM Plex Mono',monospace;} .cnt[data-bad="true"]{color:var(--red);font-weight:600;}
        .drop{border:1.5px dashed var(--line);border-radius:6px;padding:16px;text-align:center;cursor:pointer;background:#FCFDFD;display:block;}
        .drop:hover{border-color:var(--blue);background:#F4FAFD;} .drop p{margin:6px 0 0;font-size:12.5px;color:var(--sub);line-height:1.5;}
        .thumb{border:1px solid var(--line);border-radius:6px;overflow:hidden;} .thumb img{display:block;width:100%;}
        .tm{padding:8px 11px;font-size:12px;color:var(--sub);border-top:1px solid var(--line2);display:flex;justify-content:space-between;align-items:center;gap:8px;}
        .err{background:#FDECEF;border:1px solid #F5C4CD;border-radius:5px;padding:10px 12px;font-size:12.5px;line-height:1.55;color:#8C1024;margin-top:8px;}
        .rev{border-top:1px solid var(--line);flex:0 0 auto;max-height:42%;display:flex;flex-direction:column;}
        .revh{display:flex;align-items:center;justify-content:space-between;padding:11px 18px 6px;}
        .revl{overflow-y:auto;padding:0 18px 12px;}
        .fi{display:flex;gap:9px;padding:10px 0;border-top:1px solid var(--line2);}
        .fi strong{display:block;font-size:13px;font-weight:600;margin-bottom:2px;} .fi p{margin:0;font-size:12.5px;line-height:1.55;color:var(--sub);}
        .src{display:inline-block;margin-top:4px;font-family:'IBM Plex Mono',monospace;font-size:10px;color:var(--sub);background:var(--bg);border:1px solid var(--line);border-radius:3px;padding:1px 5px;}
        .teamnote{padding:10px 18px 13px;background:var(--bg);border-top:1px solid var(--line);}
        .teamnote div{font-size:12px;line-height:1.5;color:var(--sub);padding:3px 0;display:flex;gap:7px;}
        .act{display:flex;gap:8px;padding:12px 18px;border-top:1px solid var(--line);}
        .btn{border:0;border-radius:4px;padding:11px 16px;cursor:pointer;font-family:'Barlow Condensed',sans-serif;text-transform:uppercase;letter-spacing:.1em;font-weight:600;font-size:12.5px;display:flex;align-items:center;justify-content:center;gap:7px;}
        .btn.p{background:var(--blue);color:#fff;flex:1;} .btn.p:disabled{background:#E1E6E9;color:#93A2AB;cursor:not-allowed;}
        .btn.g{background:#fff;border:1px solid var(--line);color:var(--sub);} .btn.g:hover{border-color:var(--blue);color:var(--blue);}
        .ol{counter-reset:s;list-style:none;margin:0;padding:0;}
        .ol li{counter-increment:s;position:relative;padding:0 0 15px 36px;font-size:14px;line-height:1.6;}
        .ol li:before{content:counter(s);position:absolute;left:0;top:0;width:23px;height:23px;border-radius:50%;background:var(--blue);color:#fff;display:grid;place-items:center;font-size:12px;font-weight:700;font-family:'Barlow Condensed',sans-serif;}
        .warnbox{background:#FDECEF;border:1px solid #F5C4CD;border-radius:5px;padding:12px 14px;margin:6px 0 0;}
        .warnbox p{margin:0;font-size:13px;line-height:1.55;color:#8C1024;}
        .pf{display:flex;align-items:center;gap:8px;padding:7px 0;font-size:13px;border-top:1px solid var(--line2);}
        .modal{position:absolute;inset:0;background:rgba(2,16,24,.4);display:grid;place-items:center;z-index:20;padding:20px;}
        .modalin{background:#fff;border-radius:8px;padding:24px;width:480px;max-width:100%;}

        .fl{font-size:13px;font-weight:600;}
        .backlink{display:inline-flex;align-items:center;gap:6px;background:none;border:0;padding:0;
             cursor:pointer;color:var(--blue);font-size:12.5px;font-weight:600;}
        .backlink:hover{text-decoration:underline;}
        .blockname{margin:0 0 4px;font-size:17px;font-weight:600;}
        .hintbox{display:flex;gap:8px;background:#EEF6FB;border:1px solid #CBE3F0;border-radius:5px;
             padding:10px 12px;margin-top:18px;}
        .hintbox p{margin:0;font-size:12.5px;line-height:1.55;color:#1B5773;}
        .fi.fix{width:100%;text-align:left;background:none;border:0;border-top:1px solid var(--line2);
             cursor:pointer;padding:10px 0;}
        .fi.fix:hover:not(:disabled){background:#F4FAFD;}
        .fi.fix:disabled{cursor:default;}
        .metarow{display:flex;align-items:center;gap:8px;margin-top:4px;}
        .goto{font-size:11px;font-weight:600;color:var(--blue);}

        .draft{display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;
             background:#FFF6E8;border:1px solid #F2DFBE;border-radius:6px;padding:16px 18px;margin-bottom:14px;}
        .draft strong{display:block;font-size:15px;margin-bottom:3px;}
        .draft p{margin:0;font-size:12.5px;color:#7A5A20;}
        .draftact{display:flex;gap:8px;}
        .notyet{display:flex;gap:9px;background:#FFF6E8;border:1px solid #F2DFBE;border-radius:5px;
             padding:11px 13px;margin-bottom:14px;}
        .notyet p{margin:0;font-size:12.5px;line-height:1.55;color:#7A5A20;}
        .ver{font-family:'IBM Plex Mono',monospace;font-size:10px;color:#93A2AB;white-space:nowrap;}

        .brandpick{display:flex;align-items:center;gap:7px;border:1px solid var(--line);
             border-radius:20px;padding:3px 6px 3px 11px;}
        .brandpick select{border:0;background:none;font-family:'Barlow',sans-serif;font-size:13px;
             font-weight:600;color:var(--ink);cursor:pointer;padding:2px 4px;}
        .saved{font-family:'IBM Plex Mono',monospace;font-size:10px;color:#2F855A;
             border:1px solid #C6E3D0;background:#F1F9F4;border-radius:3px;padding:2px 6px;}

        .imgtotal{border-top:1px solid var(--line);background:#fff;padding:8px 14px;text-align:right;}
        .saved[data-light="true"]{color:#B98A2E;border-color:#F2DFBE;background:#FFF6E8;}
        .saved.bad{color:#8C1024;border-color:#F5C4CD;background:#FDECEF;}

        .alerts{display:flex;flex-direction:column;gap:0;background:#FDECEF;
             border-bottom:1px solid #F5C4CD;flex:0 0 auto;}
        .alerts span{display:flex;align-items:center;gap:8px;padding:9px 18px;
             font-size:12.5px;line-height:1.5;color:#8C1024;}
        .alerts span + span{border-top:1px solid #F7D5DC;}
        @media (prefers-reduced-motion:reduce){.ec *{transition:none!important;animation:none!important;}}
      `}</style>

      <div className="top">
        <div className="brandpick">
          <span className="dot" style={{ background: B_.c.primary }} />
          <select
            value={brand}
            aria-label="Brand"
            onChange={(e) => switchBrand(e.target.value)}
          >
            {Object.entries(BRANDS).map(([k, v]) => (
              <option key={k} value={k}>{v.property}</option>
            ))}
          </select>
        </div>
        {step > 0 && (
          <div className="steps">
            {[[1, "Build"], [2, "Hand over"]].map(([n, l]) => (
              <button key={n} className="stp" data-on={step === n} onClick={() => setStep(n)}><b>{n}</b>{l}</button>
            ))}
          </div>
        )}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <div className="roles">
            <button data-on={role === "requester"} onClick={() => setRole("requester")}>Requester</button>
            <button data-on={role === "team"} onClick={() => setRole("team")}>Email team</button>
          </div>
          {step > 0 && saveMode === "failed" &&
            <span className="saved bad" title="This browser refused to store the draft">Not saved</span>}
          {step > 0 && savedAt && saveMode !== "failed" &&
            <span className="saved" data-light={saveMode === "light"}
              title={saveMode === "light"
                ? "Saved, but the pictures are too large for this browser to keep — they'll need re-adding after a refresh"
                : `Saved ${new Date(savedAt).toLocaleTimeString()}`}>
              {saveMode === "light" ? "Saved · no pictures" : "Saved"}
            </span>}
          <span className="ver" title="Build version">{VERSION}</span>
          {step > 0 && <span className="lbl" style={{ color: stops.length ? "#ED0028" : "#2F855A" }}>{stops.length ? `${stops.length} to fix` : "Ready"}</span>}
        </div>
      </div>

      {(storageOff || draftCorrupt || previewDead || copyFailed) && (
        <div className="alerts">
          {storageOff && <span><AlertTriangle size={13} /> This browser won't save drafts. Your work is only kept while the tab is open.</span>}
          {draftCorrupt && <span><AlertTriangle size={13} /> A saved draft was found but couldn't be read, so it was ignored.</span>}
          {previewDead && <span><AlertTriangle size={13} /> The preview isn't responding to clicks. Reload the page.</span>}
          {copyFailed && <span><XCircle size={13} /> The copy didn't go through — nothing was put on your clipboard. Use Download instead.</span>}
        </div>
      )}

      <div className="body">
        {step === 0 && (
          <div className="pane"><div className="inner">
            {foundDraft && (
              <div className="draft">
                <div>
                  <strong>You have an unfinished request</strong>
                  <p>{BRANDS[foundDraft.brand]?.property || "Unknown brand"} · {foundDraft.meta?.subject || "no subject yet"} · saved {savedAgo(foundDraft.savedAt)}{foundDraft.imagesDropped ? " · pictures will need re-adding" : ""}</p>
                  {foundDraft.problems?.length > 0 && (
                    <p style={{ marginTop: 6, color: "#8C1024" }}>
                      {foundDraft.problems.length} block{foundDraft.problems.length === 1 ? "" : "s"} can't be restored — the library has changed since. They'll be noted for the email team.
                    </p>
                  )}
                </div>
                <div className="draftact">
                  <button className="btn p" style={{ flex: "0 0 auto", padding: "9px 16px" }} onClick={resumeDraft}>Pick up where I left off</button>
                  <button className="btn g" onClick={discardDraft}>Start fresh</button>
                </div>
              </div>
            )}
            <div className="card">
              <h3>Which brand is this for?</h3>
              <p className="sub">This sets the colours, the venue name, the email groups and which privacy footer Eloqua will append.</p>
              <div className="tiles">
                {Object.entries(BRANDS).map(([k, v]) => (
                  <button key={k} className="tile" data-on={brand === k} data-off={!v.ready} onClick={() => setBrand(k)}>
                    <span style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <span className="dot" style={{ background: v.c.bg, border: "1px solid #dfe4e7" }} />
                      <span className="dot" style={{ background: v.c.primary }} />
                      <span className="dot" style={{ background: v.c.secondary }} />
                    </span>
                    <b>{v.property}</b>
                    <span>{v.ready ? `${v.venue} · ${v.senderDomain}` : "Not set up yet"}</span>
                    {!v.ready && <em style={{ color: "#B98A2E" }}><Lock size={10} style={{ verticalAlign: -1 }} /> Needs configuring</em>}
                  </button>
                ))}
              </div>
              {B_.note && <div className="tip"><Info size={15} color="#B98A2E" style={{ flex: "0 0 auto", marginTop: 1 }} /><p>{B_.note}</p></div>}
            </div>

            <div className="card">
              <h3>Start from a template</h3>
              <p className="sub">These are your five vetted layouts. Pick the closest and change what's different.</p>
              <div className="tiles">
                {STARTERS.map((s) => (
                  <button key={s.id} className="tile" onClick={() => start(s)}>
                    <b>{s.name}</b><span>{s.desc}</span><em>{s.from}</em>
                  </button>
                ))}
              </div>
            </div>

            <div className="tip">
              <Info size={15} color="#B98A2E" style={{ flex: "0 0 auto", marginTop: 1 }} />
              <p>Whatever you build here goes to the email team to review, adjust and send. You're describing what you want, not producing the final asset — don't worry about getting it perfect.</p>
            </div>
          </div></div>
        )}

        {step === 1 && (<>
          <div className="rail">
            {GROUPS.map((g) => {
              const items = palette.filter(([, d]) => d.group === g);
              if (!items.length) return null;
              return (<div key={g}>
                <div className="rg lbl">{g}</div>
                {items.map(([t, d]) => (<button key={t} className="pb" onClick={() => add(t)} title={d.plain}><Plus size={13} />{d.label}</button>))}
              </div>);
            })}
            <button className="askbtn" onClick={() => setAsk(true)}><MessageSquarePlus size={13} />Need something else?</button>
            {asks.length > 0 && <div style={{ padding: "10px 14px", fontSize: 11.5, color: "#5D6D77", lineHeight: 1.5 }}>{asks.length} sent to the email team</div>}
          </div>

          <div className="stage">
            <div className="sbar">
              <button className="chip" data-on={device === "desktop"} onClick={() => setDevice("desktop")}><Monitor size={13} />Computer</button>
              <button className="chip" data-on={device === "mobile"} onClick={() => setDevice("mobile")}><Smartphone size={13} />Phone</button>
              <span className="lbl" style={{ marginLeft: 6 }}>Roughly how it will look · click anything to edit</span>
              {role === "team" && <button className="chip" style={{ marginLeft: "auto" }} data-on={showCode} onClick={() => setShowCode((v) => !v)}><Code2 size={13} />Code</button>}
            </div>
            {showCode && role === "team" ? (
              <div className="scroll" style={{ background: "#fff" }}>
                <pre className="mono" style={{ margin: 0, fontSize: 11.5, lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#334" }}>{clean}</pre>
              </div>
            ) : (
              <div className="scroll">
                <div className="paper" style={{ width: device === "mobile" ? 375 : 600, maxWidth: "100%" }}>
                  <iframe ref={frame} title="Preview" srcDoc={preview} scrolling="no" onLoad={wire} style={{ width: "100%", border: 0, display: "block", minHeight: 200 }} />
                </div>
              </div>
            )}
            {/* The horizontal strip of block names lived here. Removed: a vertically
                stacked email rendered as a horizontal row is a 90-degree rotation of
                the user's mental model; it truncated past nine blocks, duplicated the
                inline controls, and carried the very drag-and-drop this project has a
                decision entry against. A vertical outline panel replaces it in Phase 1. */}
            {imgTotal > 0 && (
              <div className="imgtotal">
                <span className="lbl" style={{ color: imgTotal > IMAGE_BUDGET ? "#ED0028" : undefined }}>
                  Pictures {kb(imgTotal)} / 1 MB
                </span>
              </div>
            )}
          </div>

          <div className="insp">
            {/* Two modes, never both. Stacking the request form above the block
                editor meant the editor sat below the fold and nobody found it. */}
            {!cur ? (
              <div className="iscroll">
                <div className="lbl" style={{ marginBottom: 12 }}>The request</div>

                <div className="f">
                  <label><span className="fl">What should happen</span></label>
                  <input className="inp" value={plan.goal} placeholder="Sell tickets to the Bills game"
                    ref={(el) => (fieldRefs.current.goal = el)}
                    onChange={(e) => setPlan({ ...plan, goal: e.target.value })} />
                  <p className="help">One sentence. If it needs two, the email is doing two jobs.</p>
                </div>

                <div className="f">
                  <label><span className="fl">Who it's going to</span></label>
                  <input className="inp" value={plan.audience} placeholder="Past single game buyers"
                    ref={(el) => (fieldRefs.current.audience = el)}
                    onChange={(e) => setPlan({ ...plan, audience: e.target.value })} />
                </div>

                <div className="f">
                  <label><span className="fl">Subject line</span>
                    <span className="cnt" data-bad={meta.subject.length > 50}>{meta.subject.length}/50</span></label>
                  <input className="inp" value={meta.subject}
                    ref={(el) => (fieldRefs.current.subject = el)}
                    onChange={(e) => setMeta({ ...meta, subject: e.target.value })} />
                </div>

                <div className="f">
                  <label><span className="fl">Preview line</span>
                    <span className="cnt" data-bad={meta.preheader.length > 100 || (meta.preheader.length > 0 && meta.preheader.length < 40)}>{meta.preheader.length}/100</span></label>
                  <input className="inp" value={meta.preheader}
                    ref={(el) => (fieldRefs.current.preheader = el)}
                    onChange={(e) => setMeta({ ...meta, preheader: e.target.value })} />
                  <p className="help">The grey line beside the subject. Extend it, don't repeat it.</p>
                </div>

                <div className="f">
                  <label><span className="fl">Email group</span></label>
                  <select className="sel" value={meta.emailGroupId}
                    ref={(el) => (fieldRefs.current.emailGroupId = el)}
                    onChange={(e) => setMeta({ ...meta, emailGroupId: e.target.value })}>
                    <option value="">Choose one</option>
                    {B_.groups.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
                  </select>
                  <p className="help">{B_.property} groups only. Decides which privacy footer Eloqua appends.</p>
                </div>

                <div className="f">
                  <label><span className="fl">Anything else the team should know</span></label>
                  <textarea className="ta" style={{ minHeight: 64 }} value={meta.notes}
                    placeholder="Send date, an animated GIF you want used, anything you couldn't build here"
                    onChange={(e) => setMeta({ ...meta, notes: e.target.value })} />
                </div>

                <div className="hintbox">
                  <Info size={14} color="#0080C6" style={{ flex: "0 0 auto", marginTop: 1 }} />
                  <p>Click any part of the email to edit it. Hover a part to move or remove it.</p>
                </div>
              </div>
            ) : (
              <div className="iscroll">
                <button className="backlink" onClick={() => setSel(null)}>
                  <ArrowLeft size={13} /> Back to the request
                </button>
                <div className="lbl" style={{ margin: "14px 0 6px" }}>Editing</div>
                <h4 className="blockname">{BLOCKS[cur.type].label}</h4>
                <p className="help" style={{ margin: "0 0 18px" }}>{BLOCKS[cur.type].plain}</p>

                {Object.entries(BLOCKS[cur.type].slots).map(([k, sl]) => (
                  <div className="f" key={k}>
                    <label><span className="fl">{sl.label}</span></label>
                    {sl.type === "image" ? (<>
                      {cur.slots[k] ? (
                        <div className="thumb">
                          <img src={cur.slots[k]} alt="" />
                          <div className="tm">
                            <span>{cur.imageMeta ? `${cur.imageMeta.width}px · ${kb(cur.imageMeta.bytes)}` : "Already sized"}</span>
                            <button className="btn g" style={{ padding: "4px 9px", fontSize: 11 }}
                              onClick={() => setBlocks((p) => p.map((x) => x.id === cur.id
                                ? { ...x, slots: { ...x.slots, [k]: "" }, imageBytes: 0, imageMeta: null } : x))}>Replace</button>
                          </div>
                          {cur.imageMeta && <div className="tm" style={{ color: "#2F855A" }}>
                            <span><Check size={12} style={{ verticalAlign: -2 }} />{" "}
                              {cur.imageMeta.width < cur.imageMeta.originalWidth
                                ? `Resized from ${cur.imageMeta.originalWidth}px and ${kb(cur.imageMeta.originalBytes)}`
                                : `Compressed from ${kb(cur.imageMeta.originalBytes)}`}
                            </span>
                          </div>}
                        </div>
                      ) : (
                        <label className="drop" ref={(el) => (fieldRefs.current.src = el)}>
                          <input type="file" accept="image/*" style={{ display: "none" }}
                            onChange={(e) => e.target.files?.[0] && onImage(cur.id, e.target.files[0])} />
                          {busy === cur.id ? <span style={{ fontSize: 13 }}>Resizing…</span> : <><Upload size={17} color="#0080C6" />
                            <p>Drop a picture or click to choose.<br />Resized automatically and kept under the size limit.</p></>}
                        </label>
                      )}
                      {imgErr && <div className="err">{imgErr}</div>}
                    </>) : sl.type === "textarea" ? (
                      <textarea className="ta" value={cur.slots[k] ?? ""}
                        ref={(el) => (fieldRefs.current[k] = el)}
                        onChange={(e) => set(cur.id, k, e.target.value)} />
                    ) : sl.type === "select" ? (
                      <select className="sel" value={cur.slots[k] ?? ""}
                        ref={(el) => (fieldRefs.current[k] = el)}
                        onChange={(e) => set(cur.id, k, e.target.value)}>
                        {sl.options.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
                      </select>
                    ) : (
                      <input className="inp" value={cur.slots[k] ?? ""}
                        ref={(el) => (fieldRefs.current[k] = el)}
                        onChange={(e) => set(cur.id, k, e.target.value)} />
                    )}
                    {sl.help && <p className="help">{sl.help}</p>}
                  </div>
                ))}
                {!Object.keys(BLOCKS[cur.type].slots).length && <p className="help">Nothing to fill in for this one.</p>}
              </div>
            )}

            <div className="rev">
              <div className="revh">
                <span className="lbl">Yours to fix</span>
                <span className="lbl" style={{ color: stops.length ? "#ED0028" : looks.length ? "#B98A2E" : "#2F855A" }}>
                  {stops.length ? `${stops.length} needed` : looks.length ? `${looks.length} to consider` : "All clear"}
                </span>
              </div>
              <div className="revl">
                {!yours.length && <div className="fi" style={{ borderTop: 0 }}>
                  <CheckCircle2 size={15} color="#2F855A" style={{ flex: "0 0 auto", marginTop: 1 }} />
                  <div><p>Nothing outstanding on your side.</p></div>
                </div>}
                {yours.map((f, i) => (
                  <button key={i} className="fi fix" onClick={() => jumpTo(f.target)} disabled={!f.target}>
                    {f.level === "stop"
                      ? <XCircle size={15} color="#ED0028" style={{ flex: "0 0 auto", marginTop: 1 }} />
                      : <AlertTriangle size={15} color="#B98A2E" style={{ flex: "0 0 auto", marginTop: 1 }} />}
                    <div>
                      <strong>{f.t}</strong>
                      <p>{f.w}</p>
                      <span className="metarow">
                        {f.src && <span className="src">{f.src}</span>}
                        {f.target && <span className="goto">Take me there →</span>}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="act">
              <button className="btn p" disabled={stops.length > 0} onClick={() => setStep(2)}>
                {stops.length ? `Fix ${stops.length} first` : <>Send to the email team <ArrowRight size={14} /></>}
              </button>
              <button className="btn g" onClick={() => setStep(0)}><ArrowLeft size={14} /></button>
            </div>
          </div>
        </>)}

        {step === 2 && (
          <div className="pane"><div className="inner">
            {role === "requester" ? (<>
              <div className="card">
                <h3>Ready for the email team</h3>
                <p className="sub">Here's your request. Nothing has been submitted automatically yet — send this to the email team however you normally would.</p>
                <div style={{ fontSize: 14, lineHeight: 2 }}>
                  <div><strong>Brand</strong> — {B_.property}</div>
                  <div><strong>Subject</strong> — {meta.subject || "—"}</div>
                  <div><strong>Goal</strong> — {plan.goal || "—"}</div>
                  <div><strong>Audience</strong> — {plan.audience || "not specified"}</div>
                  <div><strong>Blocks</strong> — {blocks.length} · <strong>Pictures</strong> — {kb(imgTotal)}</div>
                  {meta.notes && <div><strong>Notes</strong> — {meta.notes}</div>}
                </div>
                <div className="tip" style={{ background: "#EEF6FB", borderColor: "#CBE3F0" }}>
                  <Info size={15} color="#0080C6" style={{ flex: "0 0 auto", marginTop: 1 }} />
                  <p style={{ color: "#1B5773" }}>They'll adjust anything that needs it, test how it renders across email apps, and send. If they change something significant they'll tell you why.</p>
                </div>
              </div>
              <div className="notyet">
                <AlertTriangle size={14} color="#B98A2E" style={{ flex: "0 0 auto", marginTop: 1 }} />
                <p><strong>Not connected yet.</strong> Automatic hand-off to Asana comes later. For now this screen is a summary you can copy, and your work is saved in this browser.</p>
              </div>
              <button className="btn g" style={{ width: "100%", padding: 13 }} onClick={() => setStep(1)}><ArrowLeft size={14} /> Back to editing</button>
            </>) : (<>
              <div className="card">
                <h3>Building this in Eloqua</h3>
                <p className="sub">Six steps. The third is the one that gets missed.</p>
                <ol className="ol">
                  <li>Copy the HTML below.</li>
                  <li>Open the email in Eloqua and switch to the <strong>HTML / Source Editor</strong>. Paste this in, replacing what's there.</li>
                  <li>Click <strong>Actions → Manage Links</strong>, then <strong>Track All</strong>.
                    <div className="warnbox"><p><strong>Don't skip this.</strong> Pasted HTML isn't tracked automatically. Skip it and no clicks are recorded at all — in any email app, not just Outlook.</p></div>
                  </li>
                  <li>Confirm every button and image link is listed with Track ticked.</li>
                  <li>Run it through Email on Acid — previews, Link Validation, Image Validation.</li>
                  <li>Click the button from a real Outlook inbox to confirm it routes through Eloqua.</li>
                </ol>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button className="btn p" style={{ flex: "0 0 auto", padding: "11px 20px" }} onClick={copyHtml}>
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy the HTML</>}
                  </button>
                  <button className="btn g" onClick={() => {
                    const bl = new Blob([clean], { type: "text/html" });
                    const a = document.createElement("a"); a.href = URL.createObjectURL(bl);
                    a.download = `${meta.name.replace(/\W+/g, "-").toLowerCase()}.html`; a.click(); URL.revokeObjectURL(a.href);
                  }}>Download</button>
                </div>
              </div>

              <div className="card">
                <h3>Build checks</h3>
                <p className="sub">Run on every build. These are the things that silently break tracking or layout.</p>
                {checks.map(([l, ok], i) => (
                  <div className="pf" key={i} style={i === 0 ? { borderTop: 0 } : undefined}>
                    {ok ? <CheckCircle2 size={15} color="#2F855A" /> : <XCircle size={15} color="#ED0028" />}
                    <span style={{ color: ok ? "#021018" : "#ED0028" }}>{l}</span>
                  </div>
                ))}
                <div className="tip" style={{ background: "#EEF6FB", borderColor: "#CBE3F0" }}>
                  <Info size={15} color="#0080C6" style={{ flex: "0 0 auto", marginTop: 1 }} />
                  <p style={{ color: "#1B5773" }}>HTML is {(clean.length / 1024).toFixed(1)} KB — Gmail clips over 102 KB. Pictures total {kb(imgTotal)}, which affects load time rather than clipping.</p>
                </div>
              </div>

              {asks.length > 0 && (
                <div className="card">
                  <h3>Block requests from this build</h3>
                  <p className="sub">Things the requester wanted that the library doesn't do yet.</p>
                  {asks.map((a, i) => <div key={i} className="pf" style={i === 0 ? { borderTop: 0 } : undefined}><MessageSquarePlus size={14} color="#0080C6" /><span>{a}</span></div>)}
                </div>
              )}

              <button className="btn g" style={{ width: "100%", padding: 13 }} onClick={() => setStep(1)}><ArrowLeft size={14} /> Back to editing</button>
            </>)}
          </div></div>
        )}
      </div>

      {ask && (
        <div className="modal" onClick={() => setAsk(false)}>
          <div className="modalin" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 17 }}>Need something the blocks don't do?</h3>
              <button className="btn g" style={{ padding: 6 }} onClick={() => setAsk(false)}><X size={14} /></button>
            </div>
            <p style={{ margin: "0 0 14px", fontSize: 13.5, lineHeight: 1.55, color: "#5D6D77" }}>
              Describe it. The email team will either build it for this request or add it to the library — either way you're not stuck.
            </p>
            <textarea className="ta" value={askText} autoFocus placeholder="A countdown to the on-sale date, a four-across photo row, a table of section prices…" onChange={(e) => setAskText(e.target.value)} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="btn p" disabled={!askText.trim()} onClick={() => { setAsks((a) => [...a, askText.trim()]); setAskText(""); setAsk(false); }}><Send size={14} /> Send to the team</button>
              <button className="btn g" onClick={() => setAsk(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
